// The source behind a quote (kanban 9eb5f005): a meeting's transcript by
// speaker turn (or an email's text), opened AT the quoted words, with a find
// box to read around them. A second, narrower modal over the roadmap so the
// reader never leaves the page; Escape or Close returns to where they were.

import { useEffect, useMemo, useRef, useState } from 'react';
import { roadmapReportsApi, type SourceDocument, type SourceRef } from '../../services/roadmapReports';
import { fmtClock, matchingIndexes, paragraphs, segmentIndexForQuote, SOURCE_KIND_WORDS, textHasQuote } from './evidenceUtils';
import { fmtDayYear } from './roadmapTimelineUtils';
import styles from './RoadmapV2.module.css';

interface Props {
  source: SourceRef;
  onClose: () => void;
}

type Load = { state: 'loading' } | { state: 'ok'; doc: SourceDocument } | { state: 'failed'; status: number | null };
// What the last finished fetch brought back, stamped with the source it was for:
// `load` is derived from it, so a new source reads as loading without the effect
// having to reset state synchronously (react-hooks/set-state-in-effect).
type Fetched = { source: SourceRef; result: Exclude<Load, { state: 'loading' }> };

function errorStatus(err: unknown): number | null {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return typeof status === 'number' ? status : null;
}

export function SourceViewer({ source, onClose }: Props) {
  const [fetched, setFetched] = useState<Fetched | null>(null);
  const load: Load = fetched && fetched.source === source ? fetched.result : { state: 'loading' };
  const [term, setTerm] = useState('');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const quoteRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    roadmapReportsApi
      .getSourceDocument(source)
      .then((doc) => { if (!cancelled) setFetched({ source, result: { state: 'ok', doc } }); })
      .catch((err: unknown) => { if (!cancelled) setFetched({ source, result: { state: 'failed', status: errorStatus(err) } }); });
    return () => { cancelled = true; };
  }, [source]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const el = e.target instanceof HTMLElement ? e.target : null;
      if (el && el.tagName === 'INPUT') { el.blur(); return; }
      e.stopPropagation();
      onClose();
    };
    // Capture phase, so the initiative pop-up underneath does not close too.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const doc = load.state === 'ok' ? load.doc : null;
  // Lines: speaker turns when the delivery had them, else the text's paragraphs.
  const lines = useMemo(() => {
    if (!doc) return [] as { speaker: string | null; text: string; start: number | null }[];
    if (doc.segments.length > 0) return doc.segments;
    return paragraphs(doc.text).map((text) => ({ speaker: null, text, start: null }));
  }, [doc]);
  const quoteIdx = useMemo(() => segmentIndexForQuote(lines, source.quote), [lines, source.quote]);
  const found = useMemo(() => matchingIndexes(lines.map((l) => l.text), term), [lines, term]);
  const foundSet = useMemo(() => new Set(found), [found]);
  const quoteInText = Boolean(doc && textHasQuote(doc.text, source.quote));

  // Land on the quote once the text is in.
  useEffect(() => {
    if (!doc) return;
    const target = quoteRef.current;
    if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'center' });
    else dialogRef.current?.focus();
  }, [doc, quoteIdx]);

  const kindWord = SOURCE_KIND_WORDS[source.kind];
  const title = doc?.title ?? `${kindWord} ${source.id.slice(0, 8)}`;
  const when = doc?.started_at ? fmtDayYear(new Date(doc.started_at)) : null;

  return (
    <>
      <div className={`${styles.modalBackdrop} ${styles.sourceBackdrop}`} onClick={onClose} data-testid="source-backdrop" />
      <div
        ref={dialogRef}
        className={`${styles.modal} ${styles.sourceModal}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Source: ${title}`}
        tabIndex={-1}
        data-testid="source-viewer"
      >
        <header className={styles.modalHead}>
          <div className={styles.modalHeadMain}>
            <div className={styles.sourceKind}>{kindWord}</div>
            <h2>{title}</h2>
            <div className={styles.meta} data-testid="source-meta">
              {[when, doc?.origin].filter(Boolean).join(' · ')}
              {doc?.external_url && (
                <>
                  {' · '}
                  <a href={doc.external_url} target="_blank" rel="noreferrer">recording</a>
                </>
              )}
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} data-action="roadmap_source_close" data-outcome="close">
            Close
          </button>
        </header>

        {source.quote && (
          <div className={styles.sourceQuoteBar} data-testid="source-quote">
            <span className={styles.sourceQuoteLabel}>Quoted:</span> {source.quote}
            {doc && quoteIdx === -1 && !quoteInText && (
              <span className={styles.sourceQuoteMiss}> — not found word for word in this transcript; use Find.</span>
            )}
          </div>
        )}

        <div className={styles.sourceFind}>
          <input
            type="search"
            className={styles.respondInput}
            placeholder="Find in the text…"
            aria-label="Find in the text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          {term.trim().length >= 2 && (
            <span className={styles.meta} data-testid="source-find-count">
              {found.length} match{found.length === 1 ? '' : 'es'}
            </span>
          )}
        </div>

        <div className={styles.modalBody}>
          {load.state === 'loading' && <div className={styles.muted}>Loading the source…</div>}
          {load.state === 'failed' && (
            <div className={styles.muted} role="alert">
              {load.status === 404
                ? `This ${kindWord} is not in the database any more (id ${source.id.slice(0, 8)}).`
                : load.status === 403
                ? 'Only leadership can open the sources.'
                : 'Could not load the source — try again.'}
            </div>
          )}
          {doc && lines.length === 0 && <div className={styles.muted}>This source has no text.</div>}
          {doc && lines.length > 0 && (
            <ol className={styles.sourceLines} aria-label="Transcript">
              {lines.map((line, idx) => {
                const isQuote = idx === quoteIdx;
                const isHit = foundSet.has(idx);
                return (
                  <li
                    key={idx}
                    ref={isQuote ? (el) => { quoteRef.current = el; } : undefined}
                    className={`${styles.sourceLine} ${isQuote ? styles.sourceLineQuote : ''} ${isHit ? styles.sourceLineHit : ''}`}
                    data-testid={isQuote ? 'source-line-quote' : undefined}
                    data-hit={isHit || undefined}
                  >
                    {(line.speaker || line.start !== null) && (
                      <div className={styles.sourceSpeaker}>
                        {line.speaker ?? ''}
                        {line.start !== null && <span className={styles.sourceClock}>{fmtClock(line.start)}</span>}
                      </div>
                    )}
                    <div className={styles.sourceText}>{line.text}</div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </>
  );
}
